import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "react-bootstrap";
import Swal from "sweetalert2"; // Beautiful alerts instead of default alert()

function AddContactButton() {
  // Get accountId from URL parameters
  const { accountId } = useParams();
  const trimmedAccountId = accountId ? accountId.trim() : "";
  const navigate = useNavigate();

  // State for loading status and fetched account data
  const [isLoading, setIsLoading] = useState(false);
  const [account, setAccount] = useState(null);

  // Form data state – initialized with empty values and accountId
  const [formData, setFormData] = useState({
    title: "",
    firstName: "",
    middleInitial: "",
    lastName: "",
    role: "Dental Hygienist",
    accountId: trimmedAccountId,
  });

  // Validation error state for required fields
  const [errors, setErrors] = useState({
    firstName: false,
    lastName: false,
  });

  // Fetch account details when component mounts or accountId changes
  useEffect(() => {
    const fetchAccount = async () => {
      try {
        if (!trimmedAccountId) {
          throw new Error("Account ID is missing or empty");
        }

        const response = await fetch(
          `https://hms.backend.hiragroupcloud.com/api/account/account/${trimmedAccountId}`
        );
        const data = await response.json();

        if (response.ok) {
          setAccount(data);
        } else {
          throw new Error(data.message || "Failed to fetch account");
        }
      } catch (error) {
        // Show error with SweetAlert2 and redirect
        Swal.fire({
          icon: "error",
          title: "Error Loading Account",
          text: error.message || "Failed to load account data",
          confirmButtonColor: "#d33",
        });
        navigate("/accounts");
      }
    };

    if (trimmedAccountId) {
      fetchAccount();
    } else {
      Swal.fire({
        icon: "error",
        title: "Invalid URL",
        text: "No account ID provided",
        confirmButtonColor: "#d33",
      });
      navigate("/accounts");
    }
  }, [trimmedAccountId, navigate]);

  // Keep accountId in formData updated if it changes
  useEffect(() => {
    setFormData((prev) => ({ ...prev, accountId: trimmedAccountId }));
  }, [trimmedAccountId]);

  // Handle input changes and clear validation errors
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: false,
      }));
    }
  };

  // Save new contact to backend
  const handleSaveClick = async () => {
    if (!trimmedAccountId) {
      Swal.fire({
        icon: "error",
        title: "Missing Account",
        text: "Account ID is missing",
        confirmButtonColor: "#d33",
      });
      navigate("/accounts");
      return;
    }

    // Validate required fields
    const newErrors = {
      firstName: !formData.firstName.trim(),
      lastName: !formData.lastName.trim(),
    };

    setErrors(newErrors);

    if (newErrors.firstName || newErrors.lastName) {
      Swal.fire({
        icon: "warning",
        title: "Required Fields",
        text: "First Name and Last Name are required!",
        confirmButtonColor: "#004aad",
      });
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(
        `https://hms.backend.hiragroupcloud.com/api/contacts/${trimmedAccountId}/create`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ ...formData, accountId: trimmedAccountId }),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to save contact");
      }

      if (!account) {
        throw new Error("Account data is not available");
      }

      // Show success alert with timer and progress bar
      // Wait for the timer to finish before navigating
      await Swal.fire({
        icon: "success",
        title: "Success!",
        text: "Contact saved successfully!",
        confirmButtonColor: "#004aad",
        timer: 3000,
        timerProgressBar: true,
        showConfirmButton: false, // No OK button – only auto-close
        allowOutsideClick: false,
        allowEscapeKey: false,
      });

      // Navigate only after the alert timer finishes
      navigate(`/TabsWorking/${trimmedAccountId}`, {
        state: { accountData: account },
      });
    } catch (error) {
      // Show error message
      Swal.fire({
        icon: "error",
        title: "Save Failed",
        text: error.message || "An error occurred while saving the contact",
        confirmButtonColor: "#d33",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading while fetching account data
  if (!account) {
    return <div>Loading account data...</div>;
  }

  return (
    <>
      <div style={{ width: "95%", margin: "0 auto", padding: "0px" }}>
        <div className="row">
          <div className="col-md-10">
            {/* Page Title */}
            <h3>Add Account Contact</h3>
            <br />

            {/* Provider Details Section */}
            <h4 style={{ color: "#1E90FF" }}>Provider Details</h4>
            <div
              style={{
                backgroundColor: "#f0f0f0",
                padding: "20px",
                borderTop: "3px solid #004aad",
              }}
            >
              <div
                style={{
                  padding: "20px",
                  borderRadius: "8px",
                  maxWidth: "600px",
                  marginLeft: "0",
                  marginRight: "auto",
                  fontFamily: "Arial, sans-serif",
                }}
              >
                {/* Title Field */}
                <div style={{ marginBottom: "15px" }}>
                  <label
                    htmlFor="title"
                    style={{
                      fontWeight: "bold",
                      display: "block",
                      marginBottom: "5px",
                    }}
                  >
                    Title
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    id="title"
                    style={{
                      width: "100%",
                      padding: "8px",
                      borderRadius: "4px",
                      border: "1px solid #ccc",
                      boxSizing: "border-box",
                    }}
                  />
                </div>

                {/* First Name Field (Required) */}
                <div style={{ marginBottom: "15px" }}>
                  <label
                    htmlFor="firstName"
                    style={{
                      fontWeight: "bold",
                      display: "block",
                      marginBottom: "5px",
                    }}
                  >
                    First Name<span style={{ color: "red" }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    id="firstName"
                    style={{
                      width: "100%",
                      padding: "8px",
                      borderRadius: "4px",
                      border: errors.firstName ? "1px solid red" : "1px solid #ccc",
                      boxSizing: "border-box",
                    }}
                  />
                  {errors.firstName && (
                    <span style={{ color: "red", fontSize: "12px" }}>
                      First name is required
                    </span>
                  )}
                </div>

                {/* Middle Initial Field */}
                <div style={{ marginBottom: "15px" }}>
                  <label
                    htmlFor="middleInitial"
                    style={{
                      fontWeight: "bold",
                      display: "block",
                      marginBottom: "5px",
                    }}
                  >
                    Middle Initial
                  </label>
                  <input
                    type="text"
                    name="middleInitial"
                    value={formData.middleInitial}
                    onChange={handleInputChange}
                    id="middleInitial"
                    style={{
                      width: "100%",
                      padding: "8px",
                      borderRadius: "4px",
                      border: "1px solid #ccc",
                      boxSizing: "border-box",
                    }}
                  />
                </div>

                {/* Last Name Field (Required) */}
                <div style={{ marginBottom: "15px" }}>
                  <label
                    htmlFor="lastName"
                    style={{
                      fontWeight: "bold",
                      display: "block",
                      marginBottom: "5px",
                    }}
                  >
                    Last Name<span style={{ color: "red" }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    id="lastName"
                    style={{
                      width: "100%",
                      padding: "8px",
                      borderRadius: "4px",
                      border: errors.lastName ? "1px solid red" : "1px solid #ccc",
                      boxSizing: "border-box",
                    }}
                  />
                  {errors.lastName && (
                    <span style={{ color: "red", fontSize: "12px" }}>
                      Last name is required
                    </span>
                  )}
                </div>

                {/* Role Dropdown */}
                <div style={{ marginBottom: "15px" }}>
                  <label
                    htmlFor="role"
                    style={{
                      fontWeight: "bold",
                      display: "block",
                      marginBottom: "5px",
                    }}
                  >
                    Role
                  </label>
                  <select
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                    id="role"
                    style={{
                      width: "100%",
                      padding: "8px",
                      borderRadius: "4px",
                      border: "1px solid #ccc",
                      boxSizing: "border-box",
                      height: "35px",
                    }}
                  >
                    <option value="Dental Hygienist">Dental Hygienist</option>
                    <option value="Dental Provider">Dental Provider</option>
                    <option value="Dental Student Provider">Dental Student Provider</option>
                    <option value="Dental Faculty Provider">Dental Faculty Provider</option>
                    <option value="Dental Assistant">Dental Assistant</option>
                    <option value="Clinic Director">Clinic Director</option>
                    <option value="Office Manager">Office Manager</option>
                    <option value="Front Desk">Front Desk</option>
                    <option value="BookKeeper">BookKeeper</option>
                    <option value="Practice Mini-Lab Technician">Practice Mini-Lab Technician</option>
                    <option value="Administrator">Administrator</option>
                  </select>
                </div>

                {/* Save Button */}
                <Button
                  style={{
                    backgroundColor: "#004aad",
                    borderColor: "#004aad",
                    color: "white",
                    padding: "10px 20px",
                    borderRadius: "4px",
                    cursor: "pointer",
                    marginTop: "10px",
                  }}
                  onClick={handleSaveClick}
                  disabled={isLoading}
                >
                  {isLoading ? "Saving..." : "Save"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <br />
      <br />
    </>
  );
}

export default AddContactButton;